<template>
  <div class="upload-container">
    <div class="upload-section-card">
      <p class="upload-desc">Select PDF files to upload and start chatting about your data.</p>
      
      <!-- File Type Selection -->
      <div class="file-type-selection">
        <label for="file-type" class="file-type-label">Select Document Type *</label>
        <div class="custom-dropdown" :class="{ 'error': !selectedFileType && showFileTypeError }">
          <div 
            class="dropdown-trigger" 
            @click="filesUploaded ? null : toggleDropdown()"
            :class="{ 'open': isDropdownOpen, 'disabled': filesUploaded }"
          >
            <span class="dropdown-text">{{ selectedFileType ? getFileTypeLabel(selectedFileType) : 'Choose document type...' }}</span>
            <i class="las la-angle-down dropdown-arrow" :class="{ 'rotated': isDropdownOpen }"></i>
          </div>
          <div v-if="isDropdownOpen" class="dropdown-menu">
            <div 
              v-for="option in fileTypeOptions" 
              :key="option.value"
              class="dropdown-option"
              :class="{ 'selected': selectedFileType === option.value }"
              @click="selectFileType(option.value)"
            >
              {{ option.label }}
            </div>
          </div>
        </div>
        <div v-if="!selectedFileType && showFileTypeError" class="file-type-error">
          Please select a document type before uploading
        </div>
      </div>
      
      <!-- Upload Mode Toggle -->
      <div v-if="showBatchMode" class="upload-mode-toggle">
        <button 
          class="mode-btn" 
          :class="{ active: !batchMode }"
          @click="setUploadMode(false)"
        >
          <i class="las la-file-upload"></i>
          Single File
        </button>
        <button 
          class="mode-btn" 
          :class="{ active: batchMode }"
          @click="setUploadMode(true)"
        >
          <i class="las la-layer-group"></i>
          Batch Upload
        </button>
      </div>
      
      <!-- Progress Bar -->
      <ScanProgressBar 
        :show-progress="scanProgress.show"
        :progress-percentage="scanProgress.percentage"
        :current-page="scanProgress.currentPage"
        :total-pages="scanProgress.totalPages"
        :task-type="scanProgress.taskType"
        :progress-message="scanProgress.message"
        :batch-files="scanProgress.batchFiles"
      />
      
      <!-- Single File Upload -->
      <div v-if="!batchMode && !selectedFile && selectedFileType && selectedFileType !== 'emirates_id'" class="file-input-container" :class="{ 'disabled': !selectedFileType }">
        <input 
          type="file" 
          :accept="getFileAcceptType(selectedFileType)" 
          @change="handleFileChange" 
          ref="fileInput"
          class="file-input"
          :disabled="!selectedFileType"
          :style="{ pointerEvents: !selectedFileType ? 'none' : 'auto' }"
        />
        <div class="file-input-overlay" @click="selectedFileType ? $refs.fileInput.click() : null">
          <span class="upload-icon">📤</span>
          <p v-if="!selectedFileType">Please select document type first</p>
          <p v-else>Click to select a {{ getFileTypeDescription(selectedFileType) }} file</p>
          <p class="file-hint" v-if="selectedFileType">or drag and drop here</p>
        </div>
      </div>
      
      <!-- Batch File Upload -->
      <div v-if="batchMode && selectedFiles.length === 0 && showBatchMode" class="file-input-container" :class="{ 'disabled': !selectedFileType }">
        <input 
          type="file" 
          accept="application/pdf" 
          multiple
          @change="handleBatchFileChange" 
          ref="batchFileInput"
          class="file-input"
          :disabled="!selectedFileType"
          :style="{ pointerEvents: !selectedFileType ? 'none' : 'auto' }"
        />
        <div class="file-input-overlay" @click="selectedFileType ? $refs.batchFileInput.click() : null">
          <span class="upload-icon">📤</span>
          <p v-if="!selectedFileType">Please select document type first</p>
          <p v-else>Click to select multiple PDF files</p>
          <p class="file-hint" v-if="selectedFileType">or drag and drop here</p>
        </div>
      </div>

      <!-- Emirates ID Upload (Sequential) -->
      <div v-if="selectedFileType === 'emirates_id'" class="emirates-id-upload">
        <!-- Emirates ID Upload - Images Only -->
        <div class="emirates-id-info">
          <h4 class="emirates-id-title">
            <i class="las la-id-card"></i>
            Upload Emirates ID Images
          </h4>
          <p class="emirates-id-description">Please upload both front and back images of your Emirates ID</p>
        </div>

        <!-- Front Upload -->
        <div class="emirates-id-section">
          <h4 class="emirates-id-title">
            <i class="las la-id-card"></i>
            Emirates ID Front
          </h4>
          <div v-if="!emiratesIdFiles.front" class="file-input-container" :class="{ 'disabled': !selectedFileType }">
            <input 
              type="file" 
              accept="image/*" 
              @change="handleEmiratesIdFrontChange" 
              ref="emiratesIdFrontInput"
              class="file-input"
              :disabled="!selectedFileType"
            />
            <div class="file-input-overlay" @click="selectedFileType ? $refs.emiratesIdFrontInput.click() : null">
              <span class="upload-icon">📷</span>
              <p>Click to select front image</p>
              <p class="file-hint">JPG, PNG</p>
            </div>
          </div>
          <div v-else class="selected-file">
            <div class="file-info">
              <span class="file-name">{{ emiratesIdFiles.front.name }}</span>
              <span class="file-size">{{ formatFileSize(emiratesIdFiles.front.size) }}</span>
            </div>
            <button class="change-file-btn" @click="changeEmiratesIdFront" v-if="!uploading">
              <i class="las la-edit"></i>
              Change File
            </button>
          </div>
        </div>

        <!-- Back Upload (only show after front is uploaded) -->
        <div v-if="emiratesIdFiles.front" class="emirates-id-section">
          <h4 class="emirates-id-title">
            <i class="las la-id-card"></i>
            Emirates ID Back
          </h4>
          <div v-if="!emiratesIdFiles.back" class="file-input-container" :class="{ 'disabled': !selectedFileType }">
            <input 
              type="file" 
              accept="image/*" 
              @change="handleEmiratesIdBackChange" 
              ref="emiratesIdBackInput"
              class="file-input"
              :disabled="!selectedFileType"
            />
            <div class="file-input-overlay" @click="selectedFileType ? $refs.emiratesIdBackInput.click() : null">
              <span class="upload-icon">📷</span>
              <p>Click to select back image</p>
              <p class="file-hint">JPG, PNG</p>
            </div>
          </div>
          <div v-else class="selected-file">
            <div class="file-info">
              <span class="file-name">{{ emiratesIdFiles.back.name }}</span>
              <span class="file-size">{{ formatFileSize(emiratesIdFiles.back.size) }}</span>
            </div>
            <button class="change-file-btn" @click="changeEmiratesIdBack" v-if="!uploading">
              <i class="las la-edit"></i>
              Change File
            </button>
          </div>
        </div>

      </div>
      
      <!-- Add More Files Button -->
      <div v-if="batchMode && selectedFiles.length > 0 && !uploading && showBatchMode" class="add-more-files">
        <button class="add-more-btn" @click="addMoreFiles">
          <i class="las la-plus"></i>
          Add More Files
        </button>
        <input 
          type="file" 
          accept="application/pdf" 
          multiple
          @change="handleBatchFileChange" 
          ref="batchFileInput"
          class="hidden-file-input"
          :disabled="!selectedFileType"
          :style="{ pointerEvents: !selectedFileType ? 'none' : 'auto' }"
        />
      </div>
      
      <!-- Single Selected File -->
      <div v-if="!batchMode && selectedFile" class="selected-file">
        <div class="file-info">
          <span class="file-name">{{ selectedFile.name }}</span>
          <span class="file-size">{{ formatFileSize(selectedFile.size) }}</span>
          <span v-if="checkingPassword" class="checking-password-indicator">
            <BaseSpinner size="small" />
            Checking password protection...
          </span>
          <span v-else-if="isPasswordProtected" class="password-protected-indicator">
            <i class="las la-lock"></i>
            Password Protected
          </span>
        </div>
        <button class="change-file-btn" @click="changeFile" v-if="!uploading && !checkingPassword">
          <i class="las la-edit"></i>
          Change File
        </button>
      </div>

      <!-- Password Input Section (shown when PDF is password protected) -->
      <PasswordInput
        :show="showPasswordInput"
        :submitting="submittingPassword"
        :error="passwordError"
        input-id="pdf-password-input"
        @submit="submitPassword"
      />
      
      <!-- Batch Selected Files -->
      <div v-if="batchMode && selectedFiles.length > 0 && !uploading" class="selected-files">
        <div class="files-header">
          <h4>Selected Files ({{ selectedFiles.length }})</h4>
          <button class="clear-files-btn" @click="clearFiles" v-if="!uploading">
            <i class="las la-times"></i>
            Clear All
          </button>
        </div>
        <div class="files-list">
          <div 
            v-for="(file, index) in selectedFiles" 
            :key="index"
            class="file-item"
          >
            <div class="file-info">
              <span class="file-name">{{ file.name }}</span>
              <span class="file-size">{{ formatFileSize(file.size) }}</span>
            </div>
            <button class="remove-file-btn" @click="removeFile(index)" v-if="!uploading">
              <i class="las la-times"></i>
            </button>
          </div>
        </div>
      </div>
      
      <div class="upload-actions">
        <button 
          v-if="!batchMode && selectedFile && (!isPasswordProtected || passwordSubmitted) && selectedFileType !== 'emirates_id'" 
          class="upload-btn primary" 
          @click="uploadFile"
          :disabled="uploading || !selectedFileType || checkingPassword"
        >
          <BaseSpinner v-if="uploading" size="small" />
          <span v-if="uploading">Uploading...</span>
          <span v-else>Upload File</span>
        </button>
        <button 
          v-if="batchMode && selectedFiles.length > 0 && showBatchMode" 
          class="upload-btn primary" 
          @click="uploadBatchFiles"
          :disabled="uploading || !selectedFileType || checkingBatchPassword || (isBatchPasswordProtected && !passwordSubmitted)"
        >
          <BaseSpinner v-if="uploading" size="small" />
          <span v-if="uploading">Uploading {{ selectedFiles.length }} files...</span>
          <span v-else>Upload {{ selectedFiles.length }} Files</span>
        </button>
        <button 
          v-if="selectedFileType === 'emirates_id' && emiratesIdFiles.front && emiratesIdFiles.back" 
          class="upload-btn primary" 
          @click="uploadEmiratesId"
          :disabled="uploading"
        >
          <BaseSpinner v-if="uploading" size="small" />
          <span v-if="uploading">Uploading Emirates ID...</span>
          <span v-else>Upload Emirates ID</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import ScanProgressBar from './ScanProgressBar.vue'
import BaseSpinner from '@/components/UI/BaseSpinner.vue'
import PasswordInput from './PasswordInput.vue'
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';

// Configure worker for pdfjs-dist
// Using CDN for compatibility with version 2.16.105
pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js';

export default {
  name: 'FileUpload',
  components: {
    ScanProgressBar,
    BaseSpinner,
    PasswordInput
  },
  props: {
    scanProgress: {
      type: Object,
      default: () => ({
        show: false,
        percentage: 0,
        currentPage: 0,
        totalPages: 0,
        taskType: 'upload'
      })
    },
    preSelectedFile: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      selectedFile: null,
      selectedFiles: [],
      uploading: false,
      batchMode: false,
      selectedFileType: 'bank_statement',
      showFileTypeError: false,
      isDropdownOpen: false,
      emiratesIdFiles: {
        front: null,
        back: null
      },
      emiratesIdBackFile: null,
      filesUploaded: false, // Track if files have been uploaded
      // AECB Password properties
      filePassword: '',
      passwordSubmitted: false,
      submittingPassword: false,
      passwordError: null,
      isPasswordProtected: false,
      checkingPassword: false,
      // Batch password state
      isBatchPasswordProtected: false,
      checkingBatchPassword: false,
      fileTypeOptions: [
        { value: 'emirates_id', label: 'Emirates ID' },
        { value: 'trade_license', label: 'Trade License' },
        { value: 'vat_statement', label: 'VAT Statement' },
        { value: 'aecb_report', label: 'AECB Report' },
        { value: 'bank_statement', label: 'Bank Statement' },
        { value: 'multi_account_statement', label: 'Multi-Account Statement' },
        { value: 'payer_list', label: 'Payer List' },
        { value: 'vendor_list', label: 'Vendor List' },
        { value: 'credit_model', label: 'Credit Model' }
      ]
    }
  },
  computed: {
    showPasswordInput() {
      //Single
      if (!this.batchMode && this.selectedFile && this.isPasswordProtected && !this.passwordSubmitted) {
        return true;
      }
      //Batch
      if (this.batchMode && this.selectedFiles.length > 0 && this.isBatchPasswordProtected && !this.passwordSubmitted) {
        return true;
      }
      return false;
    },
    showBatchMode() {
      return this.selectedFileType === 'bank_statement' || this.selectedFileType === 'multi_account_statement';
    }
  },
  mounted() {
    if (this.preSelectedFile) {
      this.selectedFile = this.preSelectedFile;
    }
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    goBack() {
      this.$emit('cancel');
    },
    
    async checkPasswordProtected(file) {
      if (file.type !== 'application/pdf') {
        return false;
      }

      const reader = new FileReader();

      return new Promise((resolve) => {
        reader.onload = async () => {
          try {
            const typedArray = new Uint8Array(reader.result);
            await pdfjsLib.getDocument({ data: typedArray }).promise;
            // Successfully opened without password - not protected
            resolve(false); 
          } catch (error) {
            if (error?.name === "PasswordException") {
              // Check if it needs a password (NEED_PASSWORD = 1)
              const needPassword = pdfjsLib.PasswordResponses?.NEED_PASSWORD ?? 1;
              if (error.code === needPassword) {
                resolve(true); // Password required
              } else {
                resolve(false);
              }
            } else {
              resolve(false);
            }
          }
        };

        reader.onerror = () => {
          resolve(false); 
        };

        reader.readAsArrayBuffer(file);
      });
    },
    
    setUploadMode(isBatch) {
      this.batchMode = isBatch;
      if (isBatch) {
        this.selectedFile = null;
        this.$refs.fileInput && (this.$refs.fileInput.value = '');
      } else {
        this.selectedFiles = [];
        this.$refs.batchFileInput && (this.$refs.batchFileInput.value = '');
      }
    },
    
    async handleFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        const isValidFile = this.validateFileType(file, this.selectedFileType);
        if (isValidFile) {
          this.selectedFile = file;
          
          // Reset password state when a new file is selected
          this.isPasswordProtected = false;
          this.passwordSubmitted = false;
          this.filePassword = '';
          this.passwordError = null;
          
          if (file.type === 'application/pdf') {
            this.checkingPassword = true;
            try {
              this.isPasswordProtected = await this.checkPasswordProtected(file);
            } catch (error) {
              console.error('Error checking password protection:', error);
              this.isPasswordProtected = false;
            } finally {
              this.checkingPassword = false;
            }
          }
        } else {
          this.$refs.fileInput.value = '';
        }
      }
      this.uploading = false;
    },
    
    async handleBatchFileChange(event) {
      const files = Array.from(event.target.files);
      const validFiles = files.filter(file => file.type === 'application/pdf');
      
      if (validFiles.length !== files.length) {
        alert('Some files were not PDF files and were skipped.');
      }
      
      if (validFiles.length > 0) {
        // Filter out duplicate files by name and size
        const newFiles = validFiles.filter(newFile => 
          !this.selectedFiles.some(existingFile => 
            existingFile.name === newFile.name && existingFile.size === newFile.size
          )
        );
        
        if (newFiles.length !== validFiles.length) {
          const duplicateCount = validFiles.length - newFiles.length;
          alert(`${duplicateCount} duplicate file(s) were skipped.`);
        }
        
        if (newFiles.length > 0) {
          this.selectedFiles = [...this.selectedFiles, ...newFiles];
          // Detect if any of the batch files require a password
          if (this.selectedFileType === 'bank_statement') {
            this.checkingBatchPassword = true;
            try {
              this.isBatchPasswordProtected = await this.checkBatchPasswordProtected(this.selectedFiles);
              if (!this.isBatchPasswordProtected) {
                // Reset password state if not needed
                this.filePassword = '';
                this.passwordSubmitted = false;
                this.passwordError = null;
              }
            } catch (e) {
              console.error('Error checking batch password protection:', e);
              this.isBatchPasswordProtected = false;
            } finally {
              this.checkingBatchPassword = false;
            }
          }
        }
      }
      
      this.$refs.batchFileInput.value = '';
      this.uploading = false;
    },

    async checkBatchPasswordProtected(files) {
      const needPassword = pdfjsLib.PasswordResponses?.NEED_PASSWORD ?? 1;
      for (const file of files) {
        const requires = await this.checkPasswordProtected(file).catch(() => false);
        if (requires) return true;
      }
      return false;
    },
    
    uploadFile() {
      if (!this.selectedFileType) {
        this.showFileTypeError = true;
        return;
      }
      
      if (this.selectedFile) {
        // For password-protected files, emit the password as a third parameter
        if (this.isPasswordProtected && this.passwordSubmitted) {
          this.$emit('file-selected', this.selectedFile, this.selectedFileType, this.filePassword);
        } else {
          this.$emit('file-selected', this.selectedFile, this.selectedFileType);
        }
        this.filesUploaded = true;
      }
      this.uploading = true;
    },
    
    uploadBatchFiles() {
      if (!this.selectedFileType) {
        this.showFileTypeError = true;
        return;
      }
      
      if (this.selectedFiles.length > 0) {
        // Emit password as 3rd param if protected and submitted
        if (this.isBatchPasswordProtected && this.passwordSubmitted) {
          this.$emit('batch-files-selected', this.selectedFiles, this.selectedFileType, this.filePassword);
        } else {
          this.$emit('batch-files-selected', this.selectedFiles, this.selectedFileType);
        }
        this.filesUploaded = true;
      }
      this.uploading = true;
    },
    
    changeFile() {
      this.selectedFile = null;
      this.$refs.fileInput.value = '';
      this.$refs.fileInput.click();
      this.uploading = false;
      
      // Reset password state when changing files
      this.isPasswordProtected = false;
      this.filePassword = '';
      this.passwordSubmitted = false;
      this.submittingPassword = false;
      this.passwordError = null;
      this.checkingPassword = false;
    },
    
    removeFile(index) {
      this.selectedFiles.splice(index, 1);
    },
    
    clearFiles() {
      this.selectedFiles = [];
      this.$refs.batchFileInput.value = '';
      // Reset batch password state
      this.isBatchPasswordProtected = false;
      this.checkingBatchPassword = false;
      this.filePassword = '';
      this.passwordSubmitted = false;
      this.passwordError = null;
    },
    
    addMoreFiles() {
      if (this.selectedFileType) {
        this.$refs.batchFileInput.click();
      }
    },
    
    onFileTypeChange() {
      this.showFileTypeError = false;
    },
    
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen;
    },
    
    selectFileType(value) {
      this.selectedFileType = value;
      this.isDropdownOpen = false;
      this.showFileTypeError = false;
      
      // Reset password state when changing file type
      this.isPasswordProtected = false;
      this.filePassword = '';
      this.passwordSubmitted = false;
      this.submittingPassword = false;
      this.passwordError = null;
      this.checkingPassword = false;
      
      // Reset batch mode for non-bank statement file types since they don't support batch upload
      if (value !== 'bank_statement') {
        this.batchMode = false;
        this.selectedFiles = [];
        this.$refs.batchFileInput && (this.$refs.batchFileInput.value = '');
      }
      
      // Reset Emirates ID files when changing file type
      this.emiratesIdFiles = {
        front: null,
        back: null
      };
      this.$refs.emiratesIdFrontInput && (this.$refs.emiratesIdFrontInput.value = '');
      this.$refs.emiratesIdBackInput && (this.$refs.emiratesIdBackInput.value = '');
    },
    
    getFileTypeLabel(value) {
      const option = this.fileTypeOptions.find(opt => opt.value === value);
      return option ? option.label : value;
    },
    
    getFileAcceptType(fileType) {
      switch (fileType) {
        case 'payer_list':
        case 'vendor_list':
          return '.xlsx,.xls,.pdf';
        case 'emirates_id':
          return 'image/*';
        case 'credit_model':
          return '.xlsx,.xls';
        default:
          return 'application/pdf';
      }
    },
    
    getFileTypeDescription(fileType) {
      switch (fileType) {
        case 'payer_list':
        case 'vendor_list':
          return 'Excel or PDF';
        case 'emirates_id':
          return 'image';
        case 'credit_model':
          return 'Excel';
        default:
          return 'PDF';
      }
    },
    
    validateFileType(file, fileType) {
      switch (fileType) {
        case 'payer_list':
        case 'vendor_list':
          const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                         file.type === 'application/vnd.ms-excel';
          const isPdf = file.type === 'application/pdf';
          if (!isExcel && !isPdf) {
            alert('Please select an Excel file (.xlsx, .xls) or PDF file.');
            return false;
          }
          return true;
        case 'emirates_id':
          const isImage = file.type.startsWith('image/');
          if (!isImage) {
            alert('Please select an image file (JPG, PNG).');
            return false;
          }
          return true;
        case 'credit_model':
          const isExcelForCredit = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                                  file.type === 'application/vnd.ms-excel';
          if (!isExcelForCredit) {
            alert('Please select an Excel file (.xlsx, .xls).');
            return false;
          }
          return true;
        default:
          if (file.type !== 'application/pdf') {
            alert('Please select a PDF file.');
            return false;
          }
          return true;
      }
    },
    
    closeDropdown() {
      this.isDropdownOpen = false;
    },

    // Emirates ID specific methods - removed setEmiratesIdMode as we only support images now

    handleEmiratesIdFrontChange(event) {
      const file = event.target.files[0];
      if (file && file.type.startsWith('image/')) {
        this.emiratesIdFiles.front = file;
      } else if (file) {
        alert('Please select an image file (JPG, PNG).');
        this.$refs.emiratesIdFrontInput.value = '';
      }
    },

    handleEmiratesIdBackChange(event) {
      const file = event.target.files[0];
      if (file && file.type.startsWith('image/')) {
        this.emiratesIdFiles.back = file;
      } else if (file) {
        alert('Please select an image file (JPG, PNG).');
        this.$refs.emiratesIdBackInput.value = '';
      }
    },

    changeEmiratesIdFront() {
      this.emiratesIdFiles.front = null;
      this.$refs.emiratesIdFrontInput.value = '';
      this.$refs.emiratesIdFrontInput.click();
    },

    changeEmiratesIdBack() {
      this.emiratesIdFiles.back = null;
      this.$refs.emiratesIdBackInput.value = '';
      this.$refs.emiratesIdBackInput.click();
    },


    async uploadEmiratesId() {
      if (!this.emiratesIdFiles.front || !this.emiratesIdFiles.back) {
        alert('Please select both front and back images of your Emirates ID.');
        return;
      }

      try {
        // Create a combined file from both images
        const combinedFileName = `emirates_id_${Date.now()}.pdf`;
        const fileToUpload = new File([this.emiratesIdFiles.front], combinedFileName, {
          type: 'application/pdf'
        });
        
        // Emit to parent for upload
        this.$emit('file-selected', fileToUpload, this.selectedFileType);
        this.uploading = true;
        this.filesUploaded = true;
        
      } catch (error) {
        console.error('Error processing Emirates ID files:', error);
        alert('Error processing Emirates ID files. Please try again.');
        this.uploading = false;
      }
    },
    
    handleClickOutside(event) {
      // Use a small delay to prevent immediate closing when opening
      setTimeout(() => {
        const dropdownContainer = this.$el.querySelector('.custom-dropdown');
        if (dropdownContainer && !dropdownContainer.contains(event.target)) {
          this.closeDropdown();
        }
      }, 10);
    },
    
    formatFileSize(bytes) {
      if (!bytes) return 'Unknown';
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
    },

    async verifyPassword(file, password) {
      if (!file || file.type !== 'application/pdf') {
        return { valid: false, wrongPassword: false };
      }

      const reader = new FileReader();

      return new Promise((resolve) => {
        reader.onload = async () => {
          try {
            const typedArray = new Uint8Array(reader.result);
            const loadingTask = pdfjsLib.getDocument({ 
              data: typedArray, 
              password: password || undefined
            });
            
            await loadingTask.promise;
            resolve({ valid: true, wrongPassword: false });
          } catch (error) {
            if (error?.name === "PasswordException") {
              // Check the specific password error code
              const needPassword = pdfjsLib.PasswordResponses?.NEED_PASSWORD ?? 1;
              const incorrectPassword = pdfjsLib.PasswordResponses?.INCORRECT_PASSWORD ?? 2;
              
              if (error.code === needPassword) {
                // Still needs password (shouldn't happen if password was provided)
                resolve({ valid: false, wrongPassword: false });
              } else if (error.code === incorrectPassword) {
                // Wrong password provided
                resolve({ valid: false, wrongPassword: true });
              } else {
                resolve({ valid: false, wrongPassword: false });
              }
            } else {
              console.error('Error verifying password:', error);
              resolve({ valid: false, wrongPassword: false });
            }
          }
        };

        reader.onerror = () => {
          resolve({ valid: false, wrongPassword: false });
        };

        reader.readAsArrayBuffer(file);
      });
    },

    // Password methods
    async submitPassword(password) {
      if (!password || !password.trim()) {
        this.passwordError = 'Please enter a password';
        return;
      }

      this.submittingPassword = true;
      this.passwordError = null;

      try {
        if (this.batchMode && this.selectedFiles.length > 0) {
          // Verify same password for all files in batch
          for (const file of this.selectedFiles) {
            const res = await this.verifyPassword(file, password.trim());
            if (!res.valid) {
              this.passwordError = res.wrongPassword
                ? 'Incorrect password for one or more files. Please try again.'
                : 'Failed to verify password. Please try again.';
              this.passwordSubmitted = false;
              this.submittingPassword = false;
              return;
            }
          }
          // All files verified
          this.filePassword = password.trim();
          this.passwordSubmitted = true;
          this.uploadBatchFiles();
        } else {
          // Single file verification
          const result = await this.verifyPassword(this.selectedFile, password.trim());
          if (result.valid) {
            this.filePassword = password.trim();
            this.passwordSubmitted = true;
            this.uploadFile();
          } else if (result.wrongPassword) {
            this.passwordError = 'Incorrect password. Please try again.';
            this.passwordSubmitted = false;
          } else {
            this.passwordError = 'Failed to verify password. Please try again.';
            this.passwordSubmitted = false;
          }
        }
      } catch (error) {
        console.error('Password verification error:', error);
        this.passwordError = 'Failed to verify password. Please try again.';
        this.passwordSubmitted = false;
      } finally {
        this.submittingPassword = false;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../index.scss';

.upload-container {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  padding: 2rem;
}

.upload-section-card {
  @include content-container;
  padding: 2.5rem 2rem 2rem 2rem;
  border-radius: 0;
  border: none;
  box-shadow: none;
  background: transparent;
  width: 100%;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
}

.upload-section-card h3 {
  @extend %title;
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  text-align: center;
  color: #333333;
}

.upload-desc {
  @extend %sub-title;
  text-align: center;
  margin-bottom: 1rem;
  color: #777777;
}

.file-type-selection {
  width: 100%;
  margin-bottom: 1.5rem;
  
  .file-type-label {
    display: block;
    font-size: 0.9rem;
    font-weight: 600;
    color: #333333;
    margin-bottom: 0.5rem;
    font-family: $brand-font-medium;
  }
  
  .custom-dropdown {
    position: relative;
    width: 100%;
    
    &.error {
      .dropdown-trigger {
        border-color: #e53e3e;
        box-shadow: 0 0 0 3px rgba(#e53e3e, 0.1);
      }
    }
    
    .dropdown-trigger {
      width: 100%;
      padding: 0.75rem 3rem 0.75rem 1rem;
      border: 2px solid #e2e8f0;
      border-radius: 8px;
      font-size: 0.9rem;
      font-family: $brand-font-regular;
      background: #ffffff;
      color: #333333;
      cursor: pointer;
      transition: all 0.2s ease;
      min-height: 2.5rem;
      height: auto;
      display: flex;
      align-items: center;
      justify-content: space-between;
      position: relative;
      
      &:hover {
        border-color: darken(#e2e8f0, 10%);
      }
      
      &:focus {
        outline: none;
        border-color: $color-primary;
        box-shadow: 0 0 0 3px rgba($color-primary, 0.1);
      }
      
      &.open {
        border-color: $color-primary;
        box-shadow: 0 0 0 3px rgba($color-primary, 0.1);
      }
      
      &.disabled {
        cursor: not-allowed;
        opacity: 0.6;
        background: #f8f9fa;
        border-color: #d1d5db;
        
        .dropdown-text {
          color: #9ca3af;
        }
        
        .dropdown-arrow {
          color: #9ca3af;
        }
      }
      
      .dropdown-text {
        flex: 1;
        text-align: left;
        color: #333333;
        
        &:empty::before {
          content: 'Choose document type...';
          color: #9ca3af;
        }
      }
      
      .dropdown-arrow {
        position: absolute;
        right: 1rem;
        top: 50%;
        transform: translateY(-50%);
        transition: transform 0.2s ease;
        color: #7e8299;
        font-size: 0.8rem;
        
        &.rotated {
          transform: translateY(-50%) rotate(180deg);
        }
      }
    }
    
    .dropdown-menu {
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      background: white;
      border: 2px solid #e2e8f0;
      border-top: none;
      border-radius: 0 0 8px 8px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
      z-index: 99999;
      max-height: 240px;
      overflow-y: auto;
      margin-top: -2px;
      display: block;
      
      // Hide scrollbar but keep scrolling functionality
      scrollbar-width: none; // Firefox
      -ms-overflow-style: none; // IE and Edge
      
      &::-webkit-scrollbar {
        display: none; // Chrome, Safari, Opera
      }
      
      .dropdown-option {
        padding: 0.75rem 1rem;
        cursor: pointer;
        transition: all 0.2s ease;
        font-size: 0.9rem;
        font-family: $brand-font-regular;
        color: #333333;
        border-bottom: 1px solid #f8f9fa;
        
        &:last-child {
          border-bottom: none;
        }
        
        &:hover {
          background-color: #f8f9fa;
          color: #333333;
        }
        
        &.selected {
          background-color: $color-primary;
          color: white;
          font-weight: 500;
        }
      }
    }
  }
  
  .file-type-error {
    color: #e53e3e;
    font-size: 0.8rem;
    margin-top: 0.5rem;
    font-family: $brand-font-regular;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    
    &::before {
      content: "⚠";
      font-size: 0.7rem;
    }
  }
}

.upload-mode-toggle {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  
  .mode-btn {
    flex: 1;
    background: transparent;
    border: 1px solid #e2e8f0;
    padding: 0.75rem 1rem;
    border-radius: 6px;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    color: #6c757d;
    font-family: $brand-font-medium;
    
    &:hover {
      border-color: $color-primary;
      color: $color-primary;
    }
    
    &.active {
      background: $color-primary;
      color: $color-secondary;
      border-color: $color-primary;
    }
    
    i {
      font-size: 1rem;
    }
  }
}

.file-input-container {
  position: relative;
  width: 100%;
  height: 120px;
  border: 2px dashed #eaeaea;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover:not(.disabled) {
    border-color: $color-primary;
    background: rgba(115, 243, 169, 0.05);
  }
  
  &.disabled {
    cursor: not-allowed;
    opacity: 0.6;
    background: #f8f9fa;
    border-color: #d1d5db;
    pointer-events: none;
    
    .file-input-overlay {
      color: #9ca3af;
      pointer-events: none;
    }
    
    .file-input {
      pointer-events: none;
      cursor: not-allowed;
    }
  }
}

.file-input {
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
}

.file-input-overlay {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  pointer-events: none;
  color: #333333;
}

.upload-icon {
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

.file-hint {
  font-size: 0.9rem;
  color: #777777;
  margin-top: 0.5rem;
  font-family: $brand-font-regular;
}

.selected-file {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  width: 100%;
  border: 1px solid #eaeaea;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.file-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.checking-password-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6b7280;
  font-size: 0.85rem;
  font-family: $brand-font-regular;
}

.password-protected-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #f59e0b;
  font-size: 0.85rem;
  font-weight: 500;
  font-family: $brand-font-medium;
  
  i {
    font-size: 0.9rem;
  }
}

.file-name {
  color: #333333;
  font-weight: 500;
  word-break: break-word;
  flex: 1;
  font-family: $brand-font-medium;
}

.file-size {
  color: #777777;
  font-size: 0.9rem;
  margin-left: 1rem;
  font-family: $brand-font-regular;
}

.change-file-btn {
  background: rgba($color-primary, 0.1);
  color: $color-primary;
  border: 1px solid $color-primary;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  font-family: $brand-font-medium;
  
  &:hover {
    background: rgba($color-primary, 0.15);
    border-color: darken($color-primary, 10%);
    transform: translateY(-1px);
  }
  
  i {
    font-size: 0.8rem;
  }
}

.selected-files {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  width: 100%;
  border: 1px solid #eaeaea;
  
  .files-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    
    h4 {
      margin: 0;
      font-size: 1rem;
      color: #333333;
      font-weight: 600;
      font-family: $brand-font-medium;
    }
    
    .clear-files-btn {
      background: rgba(#e53e3e, 0.1);
      color: #e53e3e;
      border: 1px solid #e53e3e;
      padding: 0.4rem 0.8rem;
      border-radius: 6px;
      font-size: 0.8rem;
      font-weight: 500;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 0.4rem;
      transition: all 0.2s ease;
      font-family: $brand-font-medium;
      
      &:hover {
        background: rgba(#e53e3e, 0.15);
        border-color: darken(#e53e3e, 10%);
        transform: translateY(-1px);
      }
      
      i {
        font-size: 0.8rem;
      }
    }
  }
  
  .files-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    max-height: 200px;
    overflow-y: auto;
    
    // Custom scrollbar styling
    &::-webkit-scrollbar {
      width: 6px;
    }
    
    &::-webkit-scrollbar-track {
      background: transparent;
      border-radius: 3px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.1);
      border-radius: 3px;
      transition: background 0.2s ease;
      
      &:hover {
        background: rgba(0, 0, 0, 0.2);
      }
    }
    
    // Firefox scrollbar styling
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.1) transparent;
  }
  
  .file-item {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 0.75rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    transition: all 0.2s ease;
    
    &:hover {
      border-color: $color-primary;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    }
    
    .file-info {
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex: 1;
      
      .file-name {
        color: #333333;
        font-weight: 500;
        word-break: break-word;
        flex: 1;
        font-family: $brand-font-medium;
        font-size: 0.9rem;
      }
      
      .file-size {
        color: #777777;
        font-size: 0.8rem;
        margin-left: 1rem;
        font-family: $brand-font-regular;
      }
    }
    
    .remove-file-btn {
      background: rgba(#e53e3e, 0.1);
      color: #e53e3e;
      border: none;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      cursor: pointer;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      
      &:hover {
        background: rgba(#e53e3e, 0.2);
        transform: scale(1.1);
      }
      
      i {
        font-size: 0.7rem;
      }
    }
  }
}

.add-more-files {
  width: 100%;
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
  
  .add-more-btn {
    background: rgba($color-primary, 0.1);
    color: $color-primary;
    border: 1px solid $color-primary;
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    transition: all 0.2s ease;
    font-family: $brand-font-medium;
    
    &:hover {
      background: rgba($color-primary, 0.15);
      border-color: darken($color-primary, 10%);
      transform: translateY(-1px);
    }
    
    i {
      font-size: 1rem;
    }
  }
  
  .hidden-file-input {
    display: none;
  }
}

.upload-actions {
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
}

.upload-btn {
  font-family: $brand-font-medium;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  &.primary {
    background: $color-primary;
    color: $color-secondary;
    
    &:hover:not(:disabled) {
      background: adjust-color($color-primary, $lightness: -5%);
      transform: translateY(-1px);
    }
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none;
    }
  }
  
  &.secondary {
    background: $button-background;
    color: $color-secondary;
    border: 1px solid #d3d3d3;
    
    &:hover {
      background: $button-background-hover;
      transform: translateY(-1px);
    }
  }
}

// Emirates ID Upload Styles
.emirates-id-upload {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-top: 1rem;
  
  .emirates-id-info {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    
    .emirates-id-description {
      color: #6b7280;
      font-size: 0.9rem;
      margin: 0;
      font-family: $brand-font-regular;
    }
  }
  
  .emirates-id-section {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    
    .emirates-id-title {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 1rem;
      font-weight: 600;
      color: #2d3748;
      margin: 0;
      font-family: $brand-font-medium;
      
      i {
        color: $color-primary;
        font-size: 1.1rem;
      }
    }
    
    .file-input-container {
      margin: 0;
    }
  }
}

@media (max-width: 768px) {
  .emirates-id-upload {
    gap: 1rem;
    
    .emirates-id-section {
      .emirates-id-title {
        font-size: 0.9rem;
      }
    }
  }
}
</style> 